import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PawPrint, Mail, Lock, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { loginEmail, registerEmail } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.email || !form.password) { toast.error("Enter email and password"); return; }
    setBusy(true);
    try {
      if (mode === "login") await loginEmail(form.email, form.password);
      else await registerEmail(form.email, form.password, form.name);
      toast.success("Welcome to My Pack!");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authentication failed");
    }
    setBusy(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-5">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground"><PawPrint className="h-6 w-6" /></div>
          <div><div className="font-head font-extrabold text-2xl leading-none tracking-tight">My Pack</div><div className="text-xs text-muted-foreground">Health, behaviour & training</div></div>
        </div>

        <div className="rounded-2xl bg-card border border-border/70 p-7 shadow-sm">
          <h1 className="font-head text-2xl font-bold tracking-tight">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-5">{mode === "login" ? "Log in to your pack." : "Start tracking your pack for free."}</p>

          <button data-testid="google-login" onClick={google} className="w-full flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors mb-4">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> Continue with Google
          </button>
          <div className="flex items-center gap-3 mb-4"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" /></div>

          <div className="space-y-3">
            {mode === "register" && (
              <div><Label>Name</Label><div className="relative"><User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input data-testid="auth-name" className="pl-9" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" /></div></div>
            )}
            <div><Label>Email</Label><div className="relative"><Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input data-testid="auth-email" className="pl-9" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" onKeyDown={(e) => e.key === "Enter" && submit()} /></div></div>
            <div><Label>Password</Label><div className="relative"><Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input data-testid="auth-password" className="pl-9" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" onKeyDown={(e) => e.key === "Enter" && submit()} /></div></div>
            <Button data-testid="auth-submit" onClick={submit} disabled={busy} className="w-full rounded-full">{busy ? "Please wait..." : mode === "login" ? "Log in" : "Sign up free"}</Button>
          </div>

          <p className="text-sm text-center text-muted-foreground mt-5">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-primary font-medium">{mode === "login" ? "Create an account" : "Log in"}</button>
          </p>
        </div>
      </div>
    </div>
  );
}
